import React, { useState } from 'react';
import { Search, Loader2, Sparkles, X, ChevronRight } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { GoogleGenAI } from '@google/genai';
import * as ReactRouterDOM from 'react-router-dom';

const { useNavigate } = ReactRouterDOM as any;

const AiSmartSearch: React.FC = () => {
    const { getAllProducts } = useApp();
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [results, setResults] = useState<{ id: string, title: string, price: string, context: string, matchScore: number }[]>([]);
    const [suggestion, setSuggestion] = useState<string | null>(null);

    const performSearch = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (!query.trim()) return;

        setIsLoading(true);
        setIsOpen(true);
        try {
            const allProducts = getAllProducts();
            
            // Build lightweight product catalog for AI
            const catalog = allProducts.map((p: any) => ({
                id: p.id,
                title: p.title,
                desc: p.description,
                attrs: p.attributes || {}
            }));
            
            // Ask AI to rank and explain matches
            const prompt = `Użytkownik szuka: "${query}". 
Z poniższej listy produktów wybierz maksymalnie 4 najlepiej pasujące, przypisz im ocenę trafności 1-100 i napisz jedno, bardzo krótkie zdanie, dlaczego ten produkt pasuje (kontekst).
Na koniec zrób też ogólne podsumowanie/rekomendację dla użytkownika (jako 'suggestion').

Katalog:
${JSON.stringify(catalog)}

Zwróć odpowiedź w czystym formacie JSON (bez bloków markdown), struktura:
{
  "matches": [ { "id": "produkt_id", "score": 95, "context": "Idealny ze względu na dużą pojemność i niską cenę." } ],
  "suggestion": "Znalazłem kilka opcji pasujących do Twoich kryteriów. Proponuję zwrócić uwagę na produkty z największą pojemnością."
}`;

            const payload = {
                model: 'gemini-3.1-flash-lite',
                contents: [{ role: 'user', parts: [{ text: prompt }] }],
                generationConfig: { responseMimeType: 'application/json' }
            };

            const responseStream = await fetch('api/generate_ai.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!responseStream.ok) {
                let errText = await responseStream.text();
                throw new Error(`Błąd API: ${responseStream.status} ${errText}`);
            }

            const responseData = await responseStream.json();
            const rawJson = responseData.candidates?.[0]?.content?.parts?.[0]?.text || "{}";
            const parsed = JSON.parse(rawJson);

            const matchedIds = parsed.matches || [];
            
            // Re-map to full products, sort by score
            const enrichedResults = matchedIds
                .map((match: any) => {
                    const prod = allProducts.find((p: any) => p.id === match.id);
                    return prod ? {
                        id: prod.id,
                        title: prod.title,
                        price: prod.price || '0',
                        context: match.context,
                        matchScore: match.score
                    } : null;
                })
                .filter(Boolean)
                .sort((a: any, b: any) => b.matchScore - a.matchScore);

            setResults(enrichedResults);
            setSuggestion(parsed.suggestion || "Oto najlepsze wyniki wyszukiwania.");

        } catch (error) {
            console.error("AI Search Error:", error);
            // Fallback to basic text search if AI fails
            const fallback = getAllProducts().filter((p: any) => p.title.toLowerCase().includes(query.toLowerCase()));
            setResults(fallback.map((p: any) => ({ id: p.id, title: p.title, price: p.price, context: "Znaleziono po nazwie.", matchScore: 100 })));
            setSuggestion("Przeszukiwanie standardowe.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="relative z-[150] w-full max-w-sm ml-auto">
            <form onSubmit={performSearch} className="relative">
                <input 
                    type="text" 
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Opisz, czego szukasz..."
                    className="w-full bg-gray-100 border-none rounded-full py-2 pl-4 pr-10 text-sm focus:ring-2 focus:ring-indigo-500 transition-all font-sans"
                />
                <button 
                    type="submit" 
                    disabled={isLoading || !query.trim()}
                    className="absolute right-0 top-0 h-full px-3 text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
                >
                    {isLoading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                </button>
            </form>

            {isOpen && (query.length > 0 || results.length > 0) && (
                <div className="absolute top-12 right-0 w-full sm:w-[400px] bg-white shadow-2xl rounded-xl border border-indigo-50 overflow-hidden flex flex-col">
                    <div className="bg-indigo-600 p-3 text-white flex justify-between items-center shrink-0">
                        <span className="font-bold text-xs uppercase tracking-wider flex items-center gap-1"><Sparkles size={14}/> Wyniki AI</span>
                        <button onClick={() => setIsOpen(false)} className="hover:bg-white/20 p-1 rounded transition-colors"><X size={16}/></button>
                    </div>

                    <div className="max-h-[60vh] overflow-y-auto">
                        {isLoading ? (
                            <div className="p-8 flex flex-col items-center justify-center text-indigo-400 gap-3">
                                <Loader2 size={32} className="animate-spin" />
                                <p className="text-sm font-medium animate-pulse">Analizuję Twoje zapytanie...</p>
                            </div>
                        ) : (
                            <div className="p-4 space-y-4">
                                {suggestion && (
                                    <div className="bg-indigo-50 p-3 rounded text-sm text-indigo-900 border border-indigo-100">
                                        {suggestion}
                                    </div>
                                )}
                                
                                {results.length === 0 ? (
                                    <p className="text-sm text-gray-500 text-center py-4">Brak dopasowań do Twojego zapisu. Spróbuj inaczej sformułować zapytanie.</p>
                                ) : (
                                    <ul className="space-y-2">
                                        {results.map((res: any) => (
                                            <li key={res.id} className="group">
                                                <button 
                                                    onClick={() => { setIsOpen(false); navigate(`/product/${res.id}`); }}
                                                    className="w-full text-left bg-white border border-gray-100 hover:border-indigo-300 hover:bg-indigo-50/50 p-3 rounded-lg transition-colors flex items-center justify-between"
                                                >
                                                    <div className="flex-1 pr-4">
                                                        <h4 className="font-bold text-gray-900 text-sm mb-1">{res.title}</h4>
                                                        <p className="text-xs text-gray-500 leading-snug">{res.context}</p>
                                                    </div>
                                                    <div className="flex flex-col items-end gap-1 shrink-0">
                                                        <span className="font-bold text-xs bg-gray-100 px-2 py-0.5 rounded">{res.price} PLN</span>
                                                        <span className="text-[9px] text-green-600 font-bold uppercase tracking-widest">{res.matchScore}% trafności</span>
                                                    </div>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AiSmartSearch;
